import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Table,
  Button,
  Input,
  Label,
  Form,
  FormGroup,
} from "reactstrap";
import AuthService from "services/auth.service";

const ICON_OPTIONS = ["BIT2GO","USDT", "Ethereum", "USDC", "BTC", "SOL", "XRP", "Sephora", "Apple", "Walmart", "CashApp", "PayPal", "Zelle"];

const emptyForm = { 
  name: "", 
  address: "", 
  network: "", 
  type: "crypto", 
  icon: "", 
  enabled: true, 
  instructions: "", 
  requiresTag: false, 
  tagLabel: "" 
};

export default function Wallets() {
  const [wallets, setWallets] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const res = await AuthService.getWallets();
      setWallets(res.data);
    } catch (e) {
      setWallets([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    console.log('Submitting form:', form);
    
    try {
      let response;
      if (editingId) {
        response = await AuthService.updateWallet(editingId, form);
      } else {
        response = await AuthService.createWallet(form);
      }
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (error) {
      console.error('Error:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (w) => {
    console.log('Editing wallet:', w);
    setForm({ 
      name: w.name || "", 
      address: w.address || "", 
      network: w.network || "", 
      type: w.type || "crypto", 
      icon: w.icon || "", 
      enabled: w.enabled !== undefined ? w.enabled : true,
      instructions: w.instructions || "",
      requiresTag: w.requiresTag || false,
      tagLabel: w.tagLabel || ""
    });
    setEditingId(w.id);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this wallet?")) return;
    await AuthService.deleteWallet(id);
    await load();
  };

  const toggleEnabled = async (wallet) => {
    try {
      const updatedWallet = { ...wallet, enabled: !wallet.enabled };
      await AuthService.updateWallet(wallet.id, updatedWallet);
      await load();
    } catch (error) {
      console.error('Error toggling wallet:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">All Wallets</CardTitle>
            </CardHeader>
            <CardBody>
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Address/Tag</th>
                    <th>Network</th>
                    <th>Requires Tag</th>
                    <th>Enabled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ color: "green", fontWeight: 400 }}>
                  {wallets.map((w) => (
                    <tr key={w.id}>
                      <td>{w.name}</td>
                      <td>
                        <span className={`badge ${
                          w.type === 'crypto' ? 'badge-primary' : 
                          w.type === 'p2p' ? 'badge-success' : 'badge-warning'
                        }`}>
                          {w.type}
                        </span>
                      </td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {w.address}
                      </td>
                      <td>{w.network}</td>
                      <td>{w.requiresTag ? "Yes" : "No"}</td>
                      <td>
                        <Button 
                          size="sm" 
                          color={w.enabled ? "success" : "secondary"}
                          onClick={() => toggleEnabled(w)}
                        >
                          {w.enabled ? "Enabled" : "Disabled"}
                        </Button>
                      </td>
                      <td style={{ display: "flex", justifyContent: 'space-between' }}>
                        <Button size="sm" color="primary" onClick={() => onEdit(w)} style={{ marginRight: 1 }}>Edit</Button>
                        <Button size="sm" color="danger" onClick={() => onDelete(w.id)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">{editingId ? "Edit Wallet" : "Add Wallet"}</CardTitle>
            </CardHeader>
            <CardBody>
              <Form onSubmit={onSubmit}>
                <FormGroup>
                  <Label>Name *</Label>
                  <Input 
                    value={form.name} 
                    onChange={e => setForm({ ...form, name: e.target.value })} 
                    required 
                    placeholder="e.g., PayPal, CashApp, Bitcoin"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Address/Tag *</Label>
                  <Input 
                    value={form.address} 
                    onChange={e => setForm({ ...form, address: e.target.value })} 
                    required 
                    placeholder={form.type === "p2p" ? "e.g., $YourCashTag, your@paypal.com" : "Wallet address"}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Network</Label>
                  <Input 
                    value={form.network} 
                    onChange={e => setForm({ ...form, network: e.target.value })} 
                    placeholder={form.type === "p2p" ? "e.g., CashApp, PayPal, Zelle" : "e.g., ERC20, TRC20"}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Type</Label>
                  <Input 
                    type="select" 
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="crypto">Cryptocurrency</option>
                    <option value="bit2go">Bit2go</option>
                    <option value="p2p">P2P Payment</option>
                    <option value="giftcard">Gift Card</option>
                  </Input>
                </FormGroup>

                {/* Simple and Visible P2P Section */}
                {form.type === "p2p" && (
                  <>
                    {/* VERY VISIBLE CHECKBOX */}
                    <div style={{ 
                      marginBottom: '20px',
                      padding: '15px',
                      border: '2px solid #007bff',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <label style={{ 
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        margin: 0
                      }}>
                        <input
                          type="checkbox"
                          checked={form.requiresTag || false}
                          onChange={e => setForm({ ...form, requiresTag: e.target.checked })}
                          style={{ 
                            width: '20px',
                            height: '20px',
                            marginRight: '10px',
                            cursor: 'pointer'
                          }}
                        />
                        Requires User Tag/Username
                      </label>
                    </div>

                    {/* Tag Label Input - Only shown when requiresTag is checked */}
                    {form.requiresTag && (
                      <FormGroup>
                        <Label>Tag Label *</Label>
                        <Input
                          value={form.tagLabel}
                          onChange={e => setForm({ ...form, tagLabel: e.target.value })}
                          placeholder="e.g., Cashtag, Email, Username"
                          required
                        />
                      </FormGroup>
                    )}

                    <FormGroup>
                      <Label>Instructions</Label>
                      <Input
                        type="textarea"
                        value={form.instructions}
                        onChange={e => setForm({ ...form, instructions: e.target.value })}
                        placeholder="Send payment to this username. Include your username in the payment notes for verification..."
                        rows="3"
                      />
                    </FormGroup>
                  </>
                )}

                <FormGroup>
                  <Label>Icon</Label>
                  <Input 
                    type="select" 
                    value={form.icon} 
                    onChange={e => setForm({ ...form, icon: e.target.value })}
                  >
                    <option value="">Select an icon</option>
                    {ICON_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </Input>
                </FormGroup>
                
                {/* SIMPLE NATIVE CHECKBOX FOR ENABLED */}
                <div style={{ 
                  marginBottom: '20px',
                  padding: '15px',
                  border: '2px solid #28a745',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa'
                }}>
                  <label style={{ 
                    fontSize: '16px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    margin: 0
                  }}>
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={e => setForm({ ...form, enabled: e.target.checked })}
                      style={{ 
                        width: '20px',
                        height: '20px',
                        marginRight: '10px',
                        cursor: 'pointer'
                      }}
                    />
                    Enabled (Visible to clients)
                  </label>
                </div>
                
                <div style={{ marginTop: 20 }}>
                  <Button color="primary" disabled={loading} type="submit" style={{ marginRight: '10px' }}>
                    {loading ? 'Saving...' : (editingId ? 'Update Wallet' : 'Create Wallet')}
                  </Button>
                  {editingId && (
                    <Button color="secondary" type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }}>
                      Cancel
                    </Button>
                  )}
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add CSS for badges */}
      <style jsx>{`
        .badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }
        .badge-primary { background-color: #007bff; color: white; }
        .badge-success { background-color: #28a745; color: white; }
        .badge-warning { background-color: #ffc107; color: #212529; }
      `}</style>
    </div>
  );
}